import React, { useEffect, useMemo } from 'react';
import { useApiRequest } from 'shared/auth/hooks/useApiRequest';
import styled from 'styled-components';
import { getAuthorizationHeader } from 'donor/utils';
import {
  ApiResponse,
  setErrorResponse,
} from 'shared/auth/hooks/useRequestState';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { LEGACY_API_URL } from 'shared/utils/constants';

interface FileApiData {
  type?: string;
  id?: string;
  attributes?: {
    mime?: string;
    updated_at?: string | null;
    url?: string;
  };
  links?: {
    self?: string;
  };
  relationships?: {
    account?: {
      links?: {
        self?: string;
        related?: string;
      };
    };
  };
}

interface FileApiResponseBody {
  data?: FileApiData;
  links?: {
    self?: string;
  };
}

export const getFile = async (
  fileId: string,
): Promise<ApiResponse<FileApiData>> => {
  try {
    const token = getAuthorizationHeader();
    const res = await fetch(`${LEGACY_API_URL}/files/${fileId}`, {
      headers: {
        'ti-id-token': token || '',
        accept: 'application/json',
      },
    });

    if (!res.ok) {
      let message = 'Something went wrong while sending the request';
      try {
        const body = (await res.json()) as {
          message?: string;
          Message?: string;
        };
        message = body.message || body.Message || message;
      } catch (e) {
        // ignore json parse errors
      }

      return {
        success: false,
        error: setErrorResponse({
          status: res.status,
          message,
          isAxiosError: false,
          name: res.statusText || 'File Fetch Error',
        }),
      };
    }

    const body = (await res.json()) as FileApiResponseBody;
    return { success: true, data: body.data };
  } catch (e) {
    return {
      success: false,
      error: setErrorResponse({
        status: 500,
        message: 'Something went wrong while sending the request',
        isAxiosError: false,
        name: 'File Fetch Error',
      }),
    };
  }
};

export const RenderFile: React.FC<{
  file: string | undefined;
  title: string | undefined;
}> = ({ file, title }) => {
  if (!file) return null;
  return (
    <a href={file} target="_blank" rel="noreferrer">
      <img
        src={file}
        alt={title}
        title={title}
        className="success-story-image"
      />
    </a>
  );
};

interface NarrativeFileProps {
  fileId: string | undefined | null;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.medium};
`;

const NarrativeFile: React.FC<NarrativeFileProps> = ({ fileId }) => {
  const [requestState, makeRequest] = useApiRequest<FileApiData>();
  useEffect(() => {
    if (!fileId) return;
    makeRequest(getFile(fileId));
  }, [fileId, makeRequest]);

  const fileUrl = useMemo((): string | undefined => {
    if (requestState.type !== 'REQUEST_SUCCESS') {
      return undefined;
    }
    return requestState.data?.attributes?.url;
  }, [requestState]);

  if (!fileId || !fileUrl) return null;
  return (
    <StyledDiv>
      <RenderFile file={fileUrl} title={undefined} />
    </StyledDiv>
  );
};

export default NarrativeFile;
